import { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import type { AnnotationSummary } from "../types/annotation";

type AnnotationTableProps = {
  annotations: AnnotationSummary[];
  selectedAnnotationIds: string[];
  onSelectionChange: (ids: string[]) => void;
};

const AnnotationTable = ({
  annotations,
  selectedAnnotationIds,
  onSelectionChange
}: AnnotationTableProps) => {
  const [searchText, setSearchText] = useState("");

  const normalizedSearch = useMemo(() => searchText.trim().toLowerCase(), [searchText]);

  const rows = useMemo(() => {
    if (!normalizedSearch) {
      return annotations;
    }

    return annotations.filter((annotation) => {
      const values = [annotation.name, annotation.description, annotation.storage, ...(annotation.tags ?? [])];
      return values.some(
        (value) => typeof value === "string" && value.toLowerCase().includes(normalizedSearch)
      );
    });
  }, [annotations, normalizedSearch]);

  const selectionModel = useMemo(
    () => ({ type: "include", ids: new Set(selectedAnnotationIds) } as const),
    [selectedAnnotationIds]
  );

  const columns = useMemo<GridColDef<AnnotationSummary>[]>(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 0.8,
        minWidth: 180,
        resizable: true,
        renderCell: (params) => (
          <Typography variant="body2" fontWeight={600}>
            {params.row.name}
          </Typography>
        )
      },
      {
        field: "description",
        headerName: "Description",
        flex: 1.4,
        minWidth: 240,
        resizable: true,
        renderCell: (params) => (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%"
            }}
          >
            {params.row.description || "-"}
          </Typography>
        )
      },
      {
        field: "tags",
        headerName: "Tags",
        flex: 1,
        minWidth: 220,
        sortable: false,
        resizable: true,
        renderCell: (params) => {
          const tags = params.row.tags ?? [];
          if (tags.length === 0) {
            return (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            );
          }

          return (
            <Stack direction="row" spacing={0.5} sx={{ overflow: "hidden", width: "100%" }}>
              {tags.slice(0, 3).map((tag) => (
                <Chip key={tag} label={tag} size="small" variant="outlined" />
              ))}
              {tags.length > 3 ? <Chip label={`+${tags.length - 3}`} size="small" /> : null}
            </Stack>
          );
        }
      },
      {
        field: "storage",
        headerName: "Storage",
        flex: 0.45,
        minWidth: 130,
        resizable: true,
        renderCell: (params) => (
          <Chip
            label={params.row.storage === "local" ? "Local" : "Server"}
            size="small"
            color={params.row.storage === "local" ? "default" : "info"}
            variant="outlined"
          />
        )
      },
      {
        field: "active",
        headerName: "Active",
        flex: 0.35,
        minWidth: 100,
        align: "center",
        headerAlign: "center",
        resizable: true,
        renderCell: (params) => <Checkbox checked={params.row.active} disabled />
      }
    ],
    []
  );

  return (
    <Box sx={{ width: "100%", minWidth: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
      <TextField
        value={searchText}
        onChange={(event) => setSearchText(event.target.value)}
        placeholder="Search annotations"
        size="small"
        fullWidth
        sx={{ maxWidth: { sm: 320 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          )
        }}
      />
      <Box sx={{ width: "100%", minWidth: 0, overflowX: "auto" }}>
        <DataGrid
          autoHeight
          density="compact"
          hideFooterSelectedRowCount
          checkboxSelection
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(model) => {
            let ids: string[] = [];
            if (model && typeof model === "object" && "ids" in model) {
              const idSet = (model as { ids?: Set<unknown> | Iterable<unknown> }).ids;
              const normalized = idSet instanceof Set ? Array.from(idSet) : Array.from(idSet ?? []);
              ids = normalized.map(String);
            } else if (Array.isArray(model)) {
              ids = (model as unknown[]).map(String);
            }
            onSelectionChange(ids);
          }}
          rows={rows}
          columns={columns}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 25 }
            }
          }}
          disableRowSelectionOnClick={false}
          sx={{
            minWidth: 760,
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
              py: 0.75
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default AnnotationTable;